export interface ParsedDocumentLink {
  title: string;
  url: string;
  extension: string;
}

export interface ParsedMessageContent {
  body: string;
  documents: ParsedDocumentLink[];
  documentsHeading: string | null;
  disclaimer: string | null;
}

const DOCUMENT_EXTENSIONS = /\.(pdf|docx?|pptx?|xlsx?|txt|csv|md|html?|aspx)(\?|$)/i;

const DISCLAIMER_PATTERNS = [
  /\*\*The above response is generated based on SharePoint search results\.[^*]*\*\*/i,
  /\*\*Please verify information from the original documents\.[^*]*\*\*/i,
  /\*\*The above response[^*]+\*\*/i,
];

export function isDocumentUrl(url: string): boolean {
  const lower = url.toLowerCase().split('?')[0];
  if (DOCUMENT_EXTENSIONS.test(lower)) {
    return true;
  }
  if (lower.includes('sharepoint.com')) {
    return (
      lower.includes('/shared%20documents/') ||
      lower.includes('/shareddocuments/') ||
      lower.includes('/_layouts/15/download.aspx')
    );
  }
  return false;
}

export function filenameFromUrl(url: string): string {
  try {
    const cleanUrl = url.split('?')[0];
    const parts = cleanUrl.split('/');
    const filename = parts[parts.length - 1];
    if (!filename) {
      return url;
    }
    const decoded = decodeURIComponent(filename);
    const lastDot = decoded.lastIndexOf('.');
    if (lastDot > 0) {
      return decoded.substring(0, lastDot);
    }
    return decoded;
  } catch {
    return url;
  }
}

export function extensionFromUrl(url: string): string {
  try {
    const cleanUrl = url.split('?')[0];
    const parts = decodeURIComponent(cleanUrl).split('/');
    const filename = parts[parts.length - 1] || '';
    const ext = filename.split('.').pop();
    return ext ? ext.toLowerCase() : 'file';
  } catch {
    return 'file';
  }
}

function extensionLabel(ext: string): string {
  const labels: Record<string, string> = {
    pdf: 'PDF document',
    doc: 'Word document',
    docx: 'Word document',
    ppt: 'PowerPoint',
    pptx: 'PowerPoint',
    xls: 'Excel spreadsheet',
    xlsx: 'Excel spreadsheet',
    txt: 'Text file',
    csv: 'CSV file',
    md: 'Markdown',
    html: 'Web page',
    htm: 'Web page',
  };
  return labels[ext] || `${ext.toUpperCase()} file`;
}

export function getDocumentMetaLabel(url: string): string {
  return extensionLabel(extensionFromUrl(url));
}

function normalizeTitle(title: string, url: string): string {
  let cleanTitle = title.trim().replace(/\*+/g, '').replace(/^[-*•]\s*/, '').trim();
  if (!cleanTitle || cleanTitle.startsWith('http')) {
    cleanTitle = filenameFromUrl(url);
  }
  return cleanTitle;
}

function extractDisclaimer(content: string): { content: string; disclaimer: string | null } {
  for (const pattern of DISCLAIMER_PATTERNS) {
    const match = content.match(pattern);
    if (match) {
      return {
        content: content.replace(match[0], '').trim(),
        disclaimer: match[0].replace(/\*\*/g, '').trim(),
      };
    }
  }
  return { content, disclaimer: null };
}

function collectBulletUrlPairs(text: string, seen: Set<string>): ParsedDocumentLink[] {
  const documents: ParsedDocumentLink[] = [];
  const bulletNextLine = /(?:^|\n)[-*•]\s*(.+?)\s*\n\s*(https?:\/\/[^\s\n]+)/g;
  let match: RegExpExecArray | null;

  while ((match = bulletNextLine.exec(text)) !== null) {
    const url = match[2].trim().replace(/[>)]+$/, '');
    if (!isDocumentUrl(url) || seen.has(url)) {
      continue;
    }
    seen.add(url);
    documents.push({
      title: normalizeTitle(match[1], url),
      url,
      extension: extensionFromUrl(url),
    });
  }

  return documents;
}

function collectMarkdownLinks(text: string, seen: Set<string>): ParsedDocumentLink[] {
  const documents: ParsedDocumentLink[] = [];
  const mdLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let match: RegExpExecArray | null;

  while ((match = mdLinkRegex.exec(text)) !== null) {
    const url = match[2].trim().replace(/[>)]+$/, '');
    if (!isDocumentUrl(url) || seen.has(url)) {
      continue;
    }
    seen.add(url);
    documents.push({
      title: normalizeTitle(match[1], url),
      url,
      extension: extensionFromUrl(url),
    });
  }

  return documents;
}

function stripDocumentBlocks(content: string, documents: ParsedDocumentLink[]): string {
  let cleaned = content;

  cleaned = cleaned.replace(/(\*\*)?Relevant documents(\*\*)?:?\s*\n/i, '');

  for (const doc of documents) {
    const escapedTitle = doc.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    cleaned = cleaned.replace(
      new RegExp(`[-*•]\\s*${escapedTitle}\\s*\\n\\s*${doc.url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'gi'),
      ''
    );
    cleaned = cleaned.replace(
      new RegExp(`\\[${escapedTitle}\\]\\(${doc.url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)`, 'gi'),
      ''
    );
    cleaned = cleaned.replace(doc.url, '');
  }

  return cleaned.replace(/\n{3,}/g, '\n\n').trim();
}

export function parseMessageDocumentLinks(content: string): ParsedMessageContent {
  const { content: withoutDisclaimer, disclaimer } = extractDisclaimer(content);
  const seen = new Set<string>();
  const sectionMatch = withoutDisclaimer.match(/(\*\*)?Relevant documents(\*\*)?:?\s*\n/i);

  if (sectionMatch && sectionMatch.index !== undefined) {
    const body = withoutDisclaimer.slice(0, sectionMatch.index).trim();
    const documentsSection = withoutDisclaimer.slice(sectionMatch.index + sectionMatch[0].length);
    const documents = [
      ...collectBulletUrlPairs(documentsSection, seen),
      ...collectMarkdownLinks(documentsSection, seen),
    ];

    return {
      body,
      documents,
      documentsHeading: documents.length > 0 ? 'Relevant documents' : null,
      disclaimer,
    };
  }

  const bulletDocs = collectBulletUrlPairs(withoutDisclaimer, seen);
  collectMarkdownLinks(withoutDisclaimer, seen).forEach((doc) => {
    if (!seen.has(doc.url)) {
      seen.add(doc.url);
      bulletDocs.push(doc);
    }
  });

  if (bulletDocs.length >= 2) {
    return {
      body: stripDocumentBlocks(withoutDisclaimer, bulletDocs),
      documents: bulletDocs,
      documentsHeading: 'Related documents',
      disclaimer,
    };
  }

  return {
    body: withoutDisclaimer,
    documents: [],
    documentsHeading: null,
    disclaimer,
  };
}
